import { Component, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';

@Component({
  selector: 'app-customer-form',
  standalone: false,
  template: `
    <div class="max-w-2xl mx-auto">
      <div class="p-6 md:p-8 border border-slate-200 shadow-xl bg-white rounded-2xl">
        <h3 class="text-xl md:text-2xl font-bold text-[#121219] mb-4 md:mb-6">Вашите данни</h3>

        <div class="space-y-4 md:space-y-6">
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div class="space-y-2">
              <div class="flex justify-between items-center">
                <label class="text-[#121219] font-medium flex items-center gap-2 text-sm">
                  <lucide-icon name="user" class="w-4 h-4 text-[#BF7A7A]"></lucide-icon>
                  Име *
                </label>
                @if (errors()['customer_first_name']) {
                  <span class="text-xs text-red-500 font-bold animate-pulse">{{ errors()['customer_first_name'] }}</span>
                }
              </div>
              <input
                [value]="formData().customer_first_name || ''"
                (input)="update('customer_first_name', $event)"
                placeholder="Иван"
                class="w-full rounded-xl border px-3 py-2 h-11 focus:outline-none focus:ring-1 transition-colors placeholder:text-slate-300 placeholder:font-light"
                [class]="getInputClass('customer_first_name')"
              />
            </div>

            <div class="space-y-2">
              <div class="flex justify-between items-center">
                <label class="text-[#121219] font-medium text-sm">Фамилия *</label>
                @if (errors()['customer_last_name']) {
                  <span class="text-xs text-red-500 font-bold animate-pulse">{{ errors()['customer_last_name'] }}</span>
                }
              </div>
              <input
                [value]="formData().customer_last_name || ''"
                (input)="update('customer_last_name', $event)"
                placeholder="Петров"
                class="w-full rounded-xl border px-3 py-2 h-11 focus:outline-none focus:ring-1 transition-colors placeholder:text-slate-300 placeholder:font-light"
                [class]="getInputClass('customer_last_name')"
              />
            </div>
          </div>

          <div class="space-y-2">
            <div class="flex justify-between items-center">
              <label class="text-[#121219] font-medium text-sm flex items-center gap-2">
                <lucide-icon name="phone" class="w-4 h-4 text-[#BF7A7A]"></lucide-icon>
                Телефон *
              </label>
              @if (errors()['customer_phone']) {
                <span class="text-xs text-red-500 font-bold animate-pulse">{{ errors()['customer_phone'] }}</span>
              }
            </div>
            <input
              [value]="formData().customer_phone || ''"
              (input)="update('customer_phone', $event)"
              placeholder="0888 123 456"
              class="w-full rounded-xl border px-3 py-2 h-11 focus:outline-none focus:ring-1 transition-colors placeholder:text-slate-300 placeholder:font-light"
              [class]="getInputClass('customer_phone')"
            />
          </div>

          <div class="pt-3 md:pt-4 border-t border-slate-100">
            <h4 class="text-base md:text-lg font-semibold text-[#121219] mb-3 md:mb-4 flex items-center gap-2">
              <lucide-icon name="car" class="w-5 h-5 text-[#BF7A7A]"></lucide-icon>
              Данни за автомобила
            </h4>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              
              <div class="space-y-2">
                <div class="flex justify-between items-center">
                  <label class="text-[#121219] font-medium text-sm">Марка *</label>
                  @if (errors()['car_brand']) {
                    <span class="text-xs text-red-500 font-bold animate-pulse">{{ errors()['car_brand'] }}</span>
                  }
                </div>
                <input
                  [value]="formData().car_brand || ''"
                  (input)="update('car_brand', $event)"
                  placeholder="BMW"
                  class="w-full rounded-xl border px-3 py-2 h-11 focus:outline-none focus:ring-1 transition-colors placeholder:text-slate-300 placeholder:font-light"
                  [class]="getInputClass('car_brand')"
                />
              </div>

              <div class="space-y-2">
                <div class="flex justify-between items-center">
                  <label class="text-[#121219] font-medium text-sm">Модел *</label>
                  @if (errors()['car_model']) {
                    <span class="text-xs text-red-500 font-bold animate-pulse">{{ errors()['car_model'] }}</span>
                  }
                </div>
                <input
                  [value]="formData().car_model || ''"
                  (input)="update('car_model', $event)"
                  placeholder="X5"
                  class="w-full rounded-xl border px-3 py-2 h-11 focus:outline-none focus:ring-1 transition-colors placeholder:text-slate-300 placeholder:font-light"
                  [class]="getInputClass('car_model')"
                />
              </div>

              <div class="space-y-2 relative">
                <div class="flex justify-between items-center">
                  <label class="text-[#121219] font-medium flex items-center gap-2 text-sm">
                    <lucide-icon name="calendar" class="w-4 h-4 text-[#BF7A7A]"></lucide-icon>
                    Година *
                  </label>
                  @if (errors()['car_year']) {
                    <span class="text-xs text-red-500 font-bold animate-pulse">{{ errors()['car_year'] }}</span>
                  }
                </div>
                
                <div 
                  (click)="toggleYearPicker()"
                  class="w-full rounded-xl border px-3 py-2 h-11 flex items-center justify-between cursor-pointer bg-white transition-all hover:border-[#BF7A7A]/50"
                  [class]="getInputClass('car_year')">
                  
                  <span [class]="formData().car_year ? 'text-[#121219]' : 'text-slate-300 font-light'">
                    {{ formData().car_year || 'Изберете година' }}
                  </span>
                  
                  <lucide-icon name="chevron-down" 
                    class="w-4 h-4 text-slate-400 transition-transform duration-200"
                    [class.rotate-180]="showYearPicker()">
                  </lucide-icon>
                </div>

                @if (showYearPicker()) {
                  <div class="fixed inset-0 z-40" (click)="toggleYearPicker()"></div>
                  
                  <div class="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                    <div class="grid grid-cols-4 gap-1 p-2">
                      @for (year of years; track year) {
                        <button 
                          type="button"
                          (click)="selectYear(year)"
                          class="p-2 text-sm rounded-lg hover:bg-[#BF7A7A] hover:text-white transition-colors"
                          [class]="formData().car_year == year ? 'bg-[#BF7A7A] text-white font-bold' : 'text-[#121219]'">
                          {{ year }}
                        </button>
                      }
                    </div>
                  </div>
                }
              </div>

              <div class="space-y-2">
                <div class="flex justify-between items-center">
                  <label class="text-[#121219] font-medium flex items-center gap-2 text-sm">
                    <lucide-icon name="palette" class="w-4 h-4 text-[#BF7A7A]"></lucide-icon>
                    Цвят *
                  </label>
                  @if (errors()['car_color']) {
                    <span class="text-xs text-red-500 font-bold animate-pulse">{{ errors()['car_color'] }}</span>
                  }
                </div>
                <input
                  [value]="formData().car_color || ''"
                  (input)="update('car_color', $event)"
                  placeholder="Черен"
                  class="w-full rounded-xl border px-3 py-2 h-11 focus:outline-none focus:ring-1 transition-colors placeholder:text-slate-300 placeholder:font-light"
                  [class]="getInputClass('car_color')"
                />
              </div>
            </div>
          </div>

          <div class="space-y-2 pt-3 md:pt-4">
            <label class="text-[#121219] font-medium flex items-center gap-2 text-sm">
              <lucide-icon name="message-square" class="w-4 h-4 text-[#BF7A7A]"></lucide-icon>
              Допълнителен коментар (по желание)
            </label>
            <textarea
              [value]="formData().comment || ''"
              (input)="update('comment', $event)"
              placeholder="Специфични изисквания..."
              rows="3"
              class="w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:border-[#BF7A7A] focus:ring-1 focus:ring-[#BF7A7A] placeholder:text-slate-300 placeholder:font-light"
            ></textarea>
          </div>

        </div>
      </div>
    </div>
  `
})
export class CustomerFormComponent {
  formData = input.required<any>();
  errors = input<Record<string, string>>({}); 
  formChange = output<any>();

  // State for Custom Year Picker
  showYearPicker = signal(false);
  
  readonly years = Array.from({length: 50}, (_, i) => new Date().getFullYear() - i);

  update(field: string, event: Event) {
    const val = (event.target as HTMLInputElement).value;
    this.formChange.emit({ [field]: val });
  }

  selectYear(year: number) {
    this.formChange.emit({ car_year: year.toString() }); // Ensure string consistency
    this.showYearPicker.set(false); // Close dropdown
  }

  toggleYearPicker() {
    this.showYearPicker.update(v => !v);
  }

  getInputClass(fieldName: string): string {
    const hasError = this.errors()[fieldName];
    if (hasError) {
      return 'border-red-500 text-red-900 placeholder:text-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50';
    }
    return 'border-slate-300 focus:border-[#BF7A7A] focus:ring-[#BF7A7A]';
  }
}